import { createSelector } from "reselect"
import { get, chain, first } from "lodash"
import { getModels } from "modules/research-topic-root/selectors"
import { getResearchTopicDate } from "modules/research-topic-root/selectors/dates"
import { getNextModeledDate } from "helpers/dates"
import { getUnmodeledDates } from "selectors/dates"
import { getComparisonEndDate, getComparisonWorkingPeriod, getComparisonStartDate } from "helpers/comparison-dates"
import { getInstrumentPortfolio } from "./instrument"
import { getModelId } from "./model"

export const getSecurityInstrumentEarliestDate = state => get(state, "global.data.smartTradeInstrumentDescriptors.data.model.security.availability.from")
export const getSecurityInstrumentLatestDate = state => get(state, "global.data.smartTradeInstrumentDescriptors.data.model.security.availability.to")

export const getPeriod = state => get(state, "modules.smartTrade.period")

const getDates = state => get(state, "global.data.instrumentPortfolioModelMeta.data.dates")
const getRawSelectedDate = state => get(state, "modules.smartTrade.selectedDate")
const getSelectedComparisonType = state => get(state, "modules.smartTrade.selectedComparisonType")
const getRawScorecardDateDate = state => get(state, "modules.smartTrade.scorecardDate")

export const getModelLatestDate = createSelector(
  [getModels, getModelId],
  (models, selectedModel) => chain(models).find(elem => elem.id === selectedModel).get("availability.currentDate").value(),
)

export const getModelEarliestDate = createSelector(
  [getModels, getModelId],
  (models, selectedModel) => chain(models).find(elem => elem.id === selectedModel).get("availability.securitiesStartDate").value(),
)

export const getInstrumentEarliestDate = createSelector(
  [getSecurityInstrumentEarliestDate, getInstrumentPortfolio],
  (earliestDate, portfolio) => portfolio ? portfolio.availableFrom : earliestDate,
)

export const getInstrumentLatestDate = createSelector(
  [getSecurityInstrumentLatestDate, getModelLatestDate, getInstrumentPortfolio, getDates],
  (latestDate, modelLatestDate, portfolio, dates) => {
    if (portfolio) {
      let endDate = first(dates)
      if (modelLatestDate < endDate) {
        endDate = modelLatestDate
      }
      return endDate
    }
    return latestDate
  },
)

const getWorkingPeriod = createSelector(
  [getPeriod, getModelLatestDate, getResearchTopicDate],
  getComparisonWorkingPeriod,
)

export const getEndDate = createSelector(
  [getWorkingPeriod, getModelLatestDate],
  getComparisonEndDate,
)

export const getLatestDate = createSelector(
  [getInstrumentLatestDate],
  (instrumentLatestDate) => instrumentLatestDate,
)

export const getSelectedDate = createSelector(
  [getRawSelectedDate, getLatestDate],
  (selectedDate, latestDate) => selectedDate || latestDate,
)

export const getEarliestDate = createSelector(
  [getResearchTopicDate, getSelectedComparisonType, getLatestDate, getUnmodeledDates],
  (researchTopicDate, selectedComparisonType, latestDate, unmodeledDates) => {
    const comparisonType = get(selectedComparisonType, "id")
    if (comparisonType === "performance") {
      const nextModeledDate = getNextModeledDate(researchTopicDate, latestDate, unmodeledDates)
      if (nextModeledDate <= latestDate) {
        return nextModeledDate
      }
    }
    return researchTopicDate
  },
)

export const getStartDate = createSelector(
  [getWorkingPeriod, getLatestDate, getEarliestDate, () => undefined, () => undefined, getSelectedComparisonType, () => undefined],
  getComparisonStartDate,
)

export const getScorecardDate = createSelector(
  [getRawScorecardDateDate, getRawSelectedDate, getResearchTopicDate, getLatestDate],
  (scorecardDate, selectedDate, tradeDate, latestDate) => scorecardDate || tradeDate || latestDate,
)

export const getIsSingleDate = createSelector(
  [getStartDate, getLatestDate],
  (startDate, latestDate) => startDate === latestDate,
)
